// Links inside blog articles that depend on the deployment.
//
//  - Staging-only pages: the new site's /app (the Photohawk app's page; /personal was its URL), /studio, /download
//    and their aliases are on only where the new home is (src/lib/homeFlag.ts). Where it's off (production until
//    launch), a link to one of them goes to the home page instead, so an article never links to a 404. Articles
//    say "coming soon to iPhone" in their own words, so the sentence still reads right.
//  - Pending pages: a page another change is adding (the face data policy). Until it's a public page here, a link to
//    it goes to the closest live page; once it's registered in src/lib/seo/pages.ts, links go to it directly.

import type { GuideBlock } from "@/content/guides/body";
import { findPublicPage } from "@/lib/seo/pages";
import { APP_ALIAS_PATHS, APP_PATH, DOWNLOAD_PATH, OLD_STUDIO_PATHS, STUDIO_PATH } from "@/pages/home/paths";

const STAGING_ONLY = [APP_PATH, STUDIO_PATH, DOWNLOAD_PATH, ...APP_ALIAS_PATHS, ...OLD_STUDIO_PATHS];

/** Pages other work is adding → the live page to use until then. */
export const PENDING_LINKS: Record<string, string> = {
  "/face-data-policy": "/biometric-data-retention",
};

const pathOf = (href: string) => href.split(/[?#]/)[0] || "/";

/** Whether a path is one of the new site's staging-only pages (or below one, like /personal/terms). */
export const isStagingOnlyPath = (href: string): boolean => {
  const p = pathOf(href);
  return STAGING_ONLY.some((s) => p === s || p.startsWith(`${s}/`));
};

/** Where an article's internal link goes on this deployment. */
export const blogHref = (href: string, newHome: boolean): string => {
  if (!href.startsWith("/")) return href;
  if (!newHome && isStagingOnlyPath(href)) return "/";
  // The app page's URL before (/personal, and its old #waitlist): the app page itself, /app, now that the app is on
  // the App Store.
  if ((APP_ALIAS_PATHS as readonly string[]).includes(pathOf(href))) return APP_PATH;
  const pending = PENDING_LINKS[pathOf(href)];
  if (pending && !findPublicPage(pathOf(href), newHome)) return pending;
  return href;
};

/** Rewrites the [text](/path) links in some inline Markdown. */
export const mapInlineLinks = (s: string, fn: (href: string) => string): string =>
  s.replace(/\]\((\/[^)\s]*)\)/g, (_, href: string) => `](${fn(href)})`);

/** Rewrites every internal link in some blocks. */
export const mapBlockLinks = (blocks: GuideBlock[], fn: (href: string) => string): GuideBlock[] =>
  blocks.map((b): GuideBlock => {
    const t = (s: string) => mapInlineLinks(s, fn);
    switch (b.type) {
      case "paragraph":
      case "quote":
        return { ...b, text: t(b.text) };
      case "heading":
        return { ...b, text: t(b.text) };
      case "list":
        return { ...b, items: b.items.map(t) };
      case "table":
        return { ...b, head: b.head.map(t), rows: b.rows.map((r) => r.map(t)) };
      case "callout":
        return { ...b, blocks: mapBlockLinks(b.blocks, fn) };
      case "link":
        return { ...b, to: fn(b.to) };
      default:
        return b;
    }
  });
